import { useStyles } from '@team-griffin/stylr';
import longhand from '@team-griffin/css-longhand';
import { gu2px, Theme, useTheme } from '@uds/theme-utils';
import urlLanguageParse from 'presentation/utils/urlLanguageParse';
import { useBreakpoints } from '@ux/responsive';

import { CornerEmbellishmentsType } from './CornerEmbellishments';

const cornerToPosition = (str: string) => {
  switch (str) {
    case 'topLeft':
      return '0 0';
    case 'topRight':
      return 'right 0';
    case 'bottomLeft':
      return '0 bottom';
    case 'bottomRight':
      return 'right bottom';
    default:
      return '';
  }
};

// @ts-ignore: error after upgrading to ux-configs
const gu2CornerSize = (gu: number, spacing?: number) => `${gu2px(gu, spacing)}px ${gu2px(gu, spacing)}px`;

const cornerSize = ({ theme, isMinSm, isMinMd }: { theme: Theme; isMinSm: boolean; isMinMd: boolean }) => {
  const { spacing } = theme;
  if (isMinMd) {
    return gu2CornerSize(6, spacing);
  }
  if (isMinSm) {
    return gu2CornerSize(5, spacing);
  }
  return gu2CornerSize(4, spacing);
};

const generateCornerImages = ({
  cornerEmbellishments,
  currentLanguage,
  theme,
  isMinSm,
  isMinMd,
}: {
  cornerEmbellishments: CornerEmbellishmentsType;
  currentLanguage: string;
  theme: Theme;
  isMinSm: boolean;
  isMinMd: boolean;
}) => {
  const embellishments = cornerEmbellishments ?? {};
  const size = cornerSize({
    theme,
    isMinSm,
    isMinMd,
  });
  const filteredEmbellishments = [] as Array<{
    image: string;
    size: string;
    position: string;
    repeat: string;
  }>;
  Object.keys(embellishments).forEach((corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') => {
    const embellishment = embellishments[corner];
    if (embellishment != null) {
      filteredEmbellishments.push({
        image: `url(${urlLanguageParse(embellishment, currentLanguage)})`,
        size,
        position: cornerToPosition(corner),
        repeat: 'no-repeat',
      });
    }
  });
  return filteredEmbellishments;
};

const cornerArrayToCssProperty = (
  prop: 'image' | 'size' | 'position' | 'repeat',
  cornerImages: Array<{
    image: string;
    size: string;
    position: string;
    repeat: string;
  }>,
) => {
  const joinedPropArray: Array<string> = [];
  cornerImages.forEach((image) => {
    joinedPropArray.push(image[prop]);
  });
  return joinedPropArray.join(',');
};

export default ({
  currentLanguage,
  cornerEmbellishments,
}: {
  currentLanguage: string;
  cornerEmbellishments: CornerEmbellishmentsType;
}) => {
  const breakpoints = useBreakpoints();
  const { theme } = useTheme();
  const cornerImages = generateCornerImages({
    currentLanguage,
    cornerEmbellishments,
    ...breakpoints,
    theme,
  });
  return useStyles(
    {
      default: {
        root: {
          position: 'absolute',
          pointerEvents: 'none',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          ...longhand('background', {
            size: cornerArrayToCssProperty('size', cornerImages),
            image: cornerArrayToCssProperty('image', cornerImages),
            position: cornerArrayToCssProperty('position', cornerImages),
            repeat: cornerArrayToCssProperty('repeat', cornerImages),
          }),
        },
      },
    },
    [],
  );
};
